import { useCallback, useState } from 'react';
import axios from 'axios';
import { getSession } from 'next-auth/react';

import { api } from '@/lib/api';
import {
  buildFanficsCatalogQuery,
  createFanficsCatalogHeaders,
  fanficsCatalogApi,
  type FanficsCatalogSortBy,
} from '@/lib/fanfics-catalog-api';
import { getClientSessionOrThrow } from '@/lib/client-session-auth';

interface Fanfic {
  id: number;
  title: string;
  content: string;
  slug?: string;
  status?: string;
  rating?: string;
  userId: number;
  createdAt?: string;
  updatedAt?: string;
  heroImage?: HeroImage | null;
}

interface HeroImageSize {
  url: string | null;
  width: number | null;
  height: number | null;
  mimeType: string | null;
  filesize: number | null;
  filename: string | null;
}

interface HeroImageSizes {
  thumbnail: HeroImageSize;
  square: HeroImageSize;
  small: HeroImageSize;
  medium: HeroImageSize;
  large: HeroImageSize;
  xlarge: HeroImageSize;
  og: HeroImageSize;
}

interface HeroImage {
  id: number;
  alt: string | null;
  caption: string | null;
  updatedAt: string;
  createdAt: string;
  url: string;
  thumbnailURL: string;
  filename: string;
  mimeType: string;
  filesize: number;
  width: number;
  height: number;
  focalX: number;
  focalY: number;
  sizes: HeroImageSizes;
}

interface FanficSearchResult {
  fanfics: Fanfic[];
  hasNextPage: boolean;
  hasPrevPage: boolean;
  limit: number;
  nextPage: number | null;
  page: number;
  totalDocs: number;
  totalPages: number;
  totalItems?: number;
}

interface UseFanficsOptions {
  accessToken?: string;
}

interface CreateFanficData {
  title: string;
  content: string;
  status?: string;
  [key: string]: unknown;
}

interface UpdateFanficData {
  title?: string;
  content?: string;
  status?: string;
  [key: string]: unknown;
}

export function useFanfics(options: UseFanficsOptions = {}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { accessToken: optionsToken } = options;

  const resolveOptionalToken = useCallback(async (): Promise<string | undefined> => {
    if (optionsToken) {
      return optionsToken;
    }
    const session = await getSession();
    return session?.accessToken;
  }, [optionsToken]);

  const searchFanfics = useCallback(
    async (
      query: string,
    ): Promise<
      {
        id: string;
        name: string;
        heroImage?: HeroImage | null;
        workType: 'fanfic' | 'original';
        rating?: string;
      }[]
    > => {
      if (!query.trim()) return [];

      setLoading(true);
      setError(null);

      try {
        const token = await resolveOptionalToken();
        const fanficsQuery = buildFanficsCatalogQuery({
          type: 'fanfic',
          limit: 10,
          search: query,
        });
        const originalsQuery = buildFanficsCatalogQuery({
          type: 'original',
          limit: 10,
          search: query,
        });
        const [fanficsResponse, originalsResponse] = await Promise.all([
          fanficsCatalogApi.get<FanficSearchResult>(`/api/fanfics-custom/v2?${fanficsQuery}`, {
            headers: createFanficsCatalogHeaders(token),
          }),
          fanficsCatalogApi.get<FanficSearchResult>(`/api/fanfics-custom/v2?${originalsQuery}`, {
            headers: createFanficsCatalogHeaders(token),
          }),
        ]);

        return [
          ...fanficsResponse.data.fanfics.map((fanfic) => ({
            id: fanfic.id.toString(),
            name: fanfic.title,
            heroImage: fanfic.heroImage || null,
            workType: 'fanfic' as const,
            rating: fanfic.rating,
          })),
          ...originalsResponse.data.fanfics.map((fanfic) => ({
            id: fanfic.id.toString(),
            name: fanfic.title,
            heroImage: fanfic.heroImage || null,
            workType: 'original' as const,
            rating: fanfic.rating,
          })),
        ];
      } catch (err) {
        console.error('Error searching fanfics:', err);
        setError(err instanceof Error ? err.message : 'Failed to search fanfics');
        return [];
      } finally {
        setLoading(false);
      }
    },
    [resolveOptionalToken],
  );

  const getFanfics = useCallback(
    async (
      type: 'original' | 'fanfic' = 'fanfic',
      page: number = 1,
      limit: number = 10,
      options?: {
        search?: string;
        sortBy?: FanficsCatalogSortBy;
        fandoms?: string[];
        tags?: string[];
        universes?: string[];
        statuses?: string[];
        ratings?: string[];
        characters?: string[];
        pairingCharacters?: string[];
        directions?: string[];
        fandomSlug?: string;
        characterSlug?: string;
        pairingSlug?: string;
        tagSlug?: string;
        signal?: AbortSignal;
      },
    ): Promise<FanficSearchResult> => {
      setLoading(true);
      setError(null);

      try {
        const token = await resolveOptionalToken();
        const query = buildFanficsCatalogQuery({
          type,
          page,
          limit,
          search: options?.search,
          sortBy: options?.sortBy,
          fandoms: options?.fandoms,
          tags: options?.tags,
          universes: options?.universes,
          statuses: options?.statuses,
          ratings: options?.ratings,
          characters: options?.characters,
          pairingCharacters: options?.pairingCharacters,
          fandomSlug: options?.fandomSlug,
          characterSlug: options?.characterSlug,
          pairingSlug: options?.pairingSlug,
          tagSlug: options?.tagSlug,
          includePromo: true,
        });
        const response = await fanficsCatalogApi.get<FanficSearchResult>(
          `/api/fanfics-custom/v2?${query}`,
          {
            signal: options?.signal,
            headers: createFanficsCatalogHeaders(token),
          },
        );

        response.data.fanfics = response.data.fanfics.map((fanfic) => {
          //@ts-expect-error tag shape
          fanfic.tags = fanfic.tags.map((tag) => {
            return Object.assign(tag, {
              title: `${tag.title}${tag.isAdult || tag.is_adult ? ' 18+' : ''}`.trim(),
            });
          });
          return fanfic;
        });

        return response.data;
      } catch (err) {
        if (axios.isCancel(err)) {
          return {
            fanfics: [],
            hasNextPage: false,
            hasPrevPage: false,
            limit,
            nextPage: null,
            page,
            totalDocs: 0,
            totalPages: 0,
          };
        }
        console.error('Error fetching fanfics:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch fanfics');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [resolveOptionalToken],
  );

  const getFanfic = useCallback(
    async (id: string): Promise<Fanfic> => {
      setLoading(true);
      setError(null);

      try {
        const token = await resolveOptionalToken();
        const response = await api.get<Fanfic>(`/api/fanfics-custom/${id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        return response.data;
      } catch (err) {
        console.error('Error fetching fanfic:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch fanfic');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [resolveOptionalToken],
  );

  const createFanfic = useCallback(async (data: CreateFanficData): Promise<Fanfic> => {
    setLoading(true);
    setError(null);

    try {
      const { accessToken, userId } = await getClientSessionOrThrow();
      const response = await api.post<Fanfic>(
        '/api/fanfics-custom',
        {
          ...data,
          userId,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      return response.data;
    } catch (err) {
      console.error('Error creating fanfic:', err);
      setError(err instanceof Error ? err.message : 'Failed to create fanfic');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateFanfic = useCallback(async (id: string, data: UpdateFanficData): Promise<Fanfic> => {
    setLoading(true);
    setError(null);

    try {
      const { accessToken, userId } = await getClientSessionOrThrow();
      const response = await api.put<Fanfic>(
        `/api/fanfics-custom/${id}`,
        {
          ...data,
          userId,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      return response.data;
    } catch (err) {
      console.error('Error updating fanfic:', err);
      setError(err instanceof Error ? err.message : 'Failed to update fanfic');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteFanfic = useCallback(
    async (id: string): Promise<{ success: boolean; message: string }> => {
      setLoading(true);
      setError(null);

      try {
        const { accessToken } = await getClientSessionOrThrow();
        const response = await api.delete<{
          success: boolean;
          message: string;
        }>(`/api/fanfics-custom/${id}`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        return response.data;
      } catch (err) {
        console.error('Error deleting fanfic:', err);
        setError(err instanceof Error ? err.message : 'Failed to delete fanfic');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    searchFanfics,
    getFanfics,
    getFanfic,
    createFanfic,
    updateFanfic,
    deleteFanfic,
    loading,
    error,
  };
}
